import { useEffect, useState } from 'react';
import { ImageOff, X } from 'lucide-react';

interface IngredientImageCarouselProps {
  images: string[];
  onRemove?: (index: number) => void;
}

export function IngredientImageCarousel({ images, onRemove }: IngredientImageCarouselProps) {
  const [brokenUrls, setBrokenUrls] = useState<Set<string>>(new Set());

  useEffect(() => {
    setBrokenUrls(new Set());
  }, [images]);

  const handleError = (url: string) => {
    setBrokenUrls((prev) => {
      if (prev.has(url)) return prev;
      return new Set(prev).add(url);
    });
  };

  if (images.length === 0) return null;

  return (
    <div className="flex gap-3 overflow-x-auto px-2 pt-2 pb-2">
      {images.map((url, index) => {
        const isBroken = brokenUrls.has(url);

        return (
          <div
            key={`${url}-${index}`}
            className="group relative shrink-0"
          >
            {isBroken ? (
              <div className="flex h-[140px] w-[180px] items-center justify-center rounded-xl border bg-muted">
                <ImageOff className="h-8 w-8 text-muted-foreground" />
              </div>
            ) : (
              <img
                src={url}
                alt={`Ingredient image ${index + 1}`}
                className="h-[140px] w-[180px] rounded-xl border object-contain"
                onError={() => handleError(url)}
              />
            )}

            {onRemove ? (
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5 text-destructive-foreground shadow-sm transition-opacity opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(pointer:coarse)]:opacity-100"
                aria-label={`Remove image ${index + 1}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
